/* MomentPatch: native Shopify variants + multipart photo uploads. No app or analytics dependency. */
(() => {
  'use strict';
  const MAX_FILE_SIZE = 10 * 1024 * 1024;
  const ALLOWED_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
  const findVariant = (variants, photos, finish) => variants.find(v => Number(v.photos) === Number(photos) && v.finish === finish);
  const validateFiles = (files, count, exact = false) => {
    if (files.length > count) return `This bundle includes ${count} photo${count === 1 ? '' : 's'}. Remove ${files.length - count} or choose a larger bundle.`;
    for (const file of files) {
      if (!ALLOWED_TYPES.has(file.type)) return `${file.name}: please choose a JPG, PNG or WebP image.`;
      if (file.size === 0) return `${file.name} is empty. Please choose another photo.`;
      if (file.size > MAX_FILE_SIZE) return `${file.name} is over 10 MB. Please choose a smaller version.`;
    }
    if (exact && files.length !== count) return `Please add ${count} photo${count === 1 ? '' : 's'} for this bundle. You have selected ${files.length}.`;
    return '';
  };
  const formatPrice = (cents, currency) => currency === 'AUD'
    ? `A$${(cents / 100).toFixed(2)}`
    : new Intl.NumberFormat('en-AU', { style: 'currency', currency }).format(cents / 100);

  // Export pure rules for local unit tests; browser code below has no CommonJS dependency.
  if (typeof module !== 'undefined' && module.exports) module.exports = { findVariant, validateFiles, formatPrice };
  if (typeof document === 'undefined') return;
  document.documentElement.classList.replace('no-js', 'js');

  function initProduct(section) {
    if (section.dataset.initialized) return;
    section.dataset.initialized = 'true';
    const config = JSON.parse(section.querySelector('[data-memory-config]').textContent);
    const form = section.querySelector('[data-product-form]');
    const picker = form.querySelector('[data-file-picker]');
    const error = form.querySelector('[data-form-error]');
    const button = form.querySelector('[data-add-to-cart]');
    const previews = form.querySelector('[data-upload-previews]');
    const fileUrls = new Set();
    let files = [];
    let submitting = false;
    let needsCartReview = false;
    let chosen = null;

    const message = (text, focus = false) => {
      error.textContent = text;
      error.hidden = !text;
      if (text && focus) error.focus({ preventScroll: true });
    };
    const values = () => ({
      count: Number(form.querySelector('[name="photo_bundle"]:checked').value),
      finish: form.querySelector('[name="finish"]:checked').value
    });
    const refreshFiles = () => {
      fileUrls.forEach(url => URL.revokeObjectURL(url));
      fileUrls.clear();
      previews.replaceChildren();
      const { count } = values();
      files.forEach((file, index) => {
        const figure = document.createElement('figure');
        figure.className = 'upload-preview';
        const img = document.createElement('img');
        const url = URL.createObjectURL(file);
        fileUrls.add(url);
        img.src = url;
        img.alt = `Selected photo ${index + 1}`;
        const caption = document.createElement('figcaption');
        caption.textContent = file.name;
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'remove-photo';
        remove.textContent = '×';
        remove.setAttribute('aria-label', `Remove photo ${index + 1}: ${file.name}`);
        remove.addEventListener('click', () => {
          if (submitting) return;
          files.splice(index, 1);
          refreshFiles();
          message(validateFiles(files, values().count));
          picker.focus({ preventScroll: true });
        });
        figure.append(img, remove, caption);
        previews.append(figure);
      });
      form.querySelector('[data-upload-title]').textContent = files.length ? 'Add or change photos' : `Add ${count} photo${count === 1 ? '' : 's'}`;
      form.querySelector('[data-upload-count]').textContent = `${files.length} of ${count} selected`;
    };
    const update = (updateUrl = true) => {
      const { count, finish } = values();
      chosen = findVariant(config.variants, count, finish);
      form.querySelectorAll('.bundle-option').forEach(label => label.classList.toggle('is-selected', label.querySelector('input').checked));
      form.querySelector('[data-variant-id]').value = chosen?.id || '';
      form.querySelector('[data-photo-count-property]').value = count;
      form.querySelectorAll('[data-price-for]').forEach(el => {
        const variant = findVariant(config.variants, Number(el.dataset.priceFor), finish);
        el.textContent = variant ? formatPrice(variant.price, config.currency) : 'Unavailable';
      });
      button.disabled = !chosen?.available || submitting || needsCartReview;
      form.querySelector('[data-cart-label]').textContent = chosen?.available
        ? `Add to cart — ${formatPrice(chosen.price, config.currency)}` : 'This option is unavailable';
      form.querySelector('[data-shipping-note]').textContent = count > 1
        ? 'Free Australian shipping with this bundle.' : 'Shipping calculated at checkout.';
      const answer = document.querySelector('[data-receive-answer]');
      if (answer) answer.textContent = `${count === 1 ? 'One personalised sheet' : count === 2 ? 'Two personalised sheets' : 'Four personalised sheets'}, ${count === 1 ? 'with' : 'each with'} six peelable stickers, plus your digital artwork.`;
      if (updateUrl && chosen) {
        const url = new URL(window.location.href);
        if (config.preview) { url.searchParams.set('photos', count); url.searchParams.set('finish', finish); }
        else { url.searchParams.set('variant', chosen.id); url.searchParams.delete('photos'); url.searchParams.delete('finish'); }
        window.history.replaceState({}, '', url);
      }
      refreshFiles();
      message(validateFiles(files, count));
    };
    const params = new URLSearchParams(window.location.search);
    const linked = config.variants.find(v => String(v.id) === params.get('variant'));
    const initialCount = linked?.photos || ([1, 2, 4].includes(Number(params.get('photos'))) ? Number(params.get('photos')) : 2);
    const initialFinish = linked?.finish || (params.get('finish') === 'Gloss' ? 'Gloss' : 'Matte');
    form.querySelector(`[name="photo_bundle"][value="${initialCount}"]`).checked = true;
    form.querySelector(`[name="finish"][value="${initialFinish}"]`).checked = true;
    form.querySelectorAll('[name="photo_bundle"], [name="finish"]').forEach(input => input.addEventListener('change', () => update()));

    const addFiles = incoming => {
      if (submitting) return;
      const newFiles = [...incoming];
      const candidate = [...files, ...newFiles];
      const issue = validateFiles(candidate, values().count);
      if (issue) { message(issue, true); return; }
      files = candidate;
      refreshFiles();
      message('');
    };
    picker.addEventListener('change', () => { addFiles(picker.files); picker.value = ''; });
    const dropZone = form.querySelector('[data-drop-zone]');
    ['dragenter', 'dragover'].forEach(type => dropZone.addEventListener(type, event => { event.preventDefault(); dropZone.classList.add('is-dragging'); }));
    ['dragleave', 'drop'].forEach(type => dropZone.addEventListener(type, event => { event.preventDefault(); dropZone.classList.remove('is-dragging'); }));
    dropZone.addEventListener('drop', event => addFiles(event.dataTransfer.files));

    section.querySelectorAll('[data-gallery]').forEach(thumb => thumb.addEventListener('click', () => {
      const source = thumb.querySelector('img');
      const main = section.querySelector('.gallery-main .sheet-art');
      main.src = source.src;
      main.srcset = source.srcset;
      main.alt = source.alt;
      section.querySelector('.gallery-main').classList.toggle('showing-other', thumb.dataset.gallery !== 'wildflowers');
      section.querySelectorAll('[data-gallery]').forEach(item => {
        const active = item === thumb;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-pressed', String(active));
      });
    }));
    const busy = value => {
      submitting = value;
      button.disabled = value || !chosen?.available || needsCartReview;
      form.setAttribute('aria-busy', String(value));
      form.querySelectorAll('input, .remove-photo').forEach(input => input.disabled = value);
      if (value) form.querySelector('[data-cart-label]').textContent = 'Uploading your photos…';
      else update(false);
    };
    form.addEventListener('submit', async event => {
      event.preventDefault();
      if (submitting || needsCartReview) return;
      if (config.preview) { message('This is the design preview. No photos have been uploaded and no order has been placed.', true); return; }
      if (!chosen?.available) { message('Please choose an available bundle and finish.', true); return; }
      const issue = validateFiles(files, values().count, true);
      if (issue) { message(issue, true); return; }
      // FormData is essential: converting to JSON would discard the actual file bytes.
      const payload = new FormData(form);
      payload.delete('photo_bundle');
      payload.delete('finish');
      files.forEach((file, index) => payload.append(`properties[Photo ${index + 1}]`, file, file.name));
      busy(true);
      message('');
      let added = false;
      let definitelyRejected = false;
      try {
        const response = await fetch(config.addUrl, { method: 'POST', body: payload, credentials: 'same-origin', headers: { Accept: 'application/json' } });
        definitelyRejected = !response.ok;
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || 'Shopify could not add this bundle. Please try again.');
        added = true;
        const line = result.items ? result.items[0] : result;
        const photosSaved = files.every((_, index) => {
          const value = line.properties?.[`Photo ${index + 1}`];
          return typeof value === 'string' && /^(https?:\/\/|\/)/.test(value);
        });
        if (!photosSaved) throw new Error('The bundle was added, but we could not confirm its photo attachments. Please check your bag before checkout; do not add it again.');
        window.location.assign(config.cartUrl);
      } catch (err) {
        // An ambiguous response must not invite a duplicate bundle submission.
        needsCartReview = !definitelyRejected;
        const text = err instanceof TypeError
          ? 'The upload connection was interrupted. Check your bag before trying again so you don’t add the same bundle twice.'
          : (err.message || 'Something went wrong. Check your bag before trying again.');
        busy(false);
        message(text, true);
        if (needsCartReview) {
          const link = document.createElement('a');
          link.href = config.cartUrl;
          link.textContent = ' Check my bag';
          link.style.textDecoration = 'underline';
          error.append(link);
        }
      }
    });
    window.addEventListener('pagehide', () => fileUrls.forEach(url => URL.revokeObjectURL(url)), { once: true });
    update(false);
  }

  function init(root = document) {
    root.querySelectorAll('[data-product-section]').forEach(initProduct);
    root.querySelectorAll('[data-open-dialog]').forEach(button => {
      if (button.dataset.bound) return;
      button.dataset.bound = 'true';
      button.addEventListener('click', () => document.getElementById(button.dataset.openDialog)?.showModal());
    });
    root.querySelectorAll('[data-close-dialog]').forEach(button => {
      if (button.dataset.bound) return;
      button.dataset.bound = 'true';
      button.addEventListener('click', () => button.closest('dialog').close());
    });
    root.querySelectorAll('dialog').forEach(dialog => {
      if (dialog.dataset.bound) return;
      dialog.dataset.bound = 'true';
      dialog.addEventListener('click', event => { if (event.target === dialog) { const rect = dialog.getBoundingClientRect(); if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close(); } });
    });
  }
  const openHash = () => {
    if (window.location.hash === '#shipping') { const detail = document.getElementById('shipping'); if (detail) detail.open = true; }
    if (window.location.hash === '#ContactForm' || new URLSearchParams(window.location.search).has('contact_posted')) document.getElementById('contact-dialog')?.showModal();
  };
  init();
  openHash();
  window.addEventListener('hashchange', openHash);
  document.addEventListener('shopify:section:load', event => init(event.target));
})();
